/**
 * Where each diagram stands in the lobby, as pure arithmetic.
 *
 * **The arc-and-ranks shape is what answers SPEC-157's scale requirement, and it answers it
 * structurally rather than with a cap.** That spec settles that the lobby must stay usable for
 * somebody with hundreds of diagrams and calls a design that only works for a handful a wrong
 * answer rather than a partial one. Ringing everything around the viewer fails that: a hundred
 * miniatures at arm's length either overlap or shrink to nothing. Here the nearest rank is a
 * shallow arc of a fixed few, and everything else is *behind* it — further away, smaller, and
 * (the part that matters for a frame budget) rendered at lower detail or not at all. Bringing a
 * rank forward is how you reach the rest, so a hundred diagrams is more ranks, never a longer
 * wall to walk or a smaller thing to squint at (SPEC-162).
 *
 * Kept free of Babylon deliberately. Everything here is answerable with numbers, and a layout
 * that can only be checked by looking at a rendered frame is a layout this project cannot test
 * at all — `NullEngine` never rasterises. What a scene *does* with these positions is
 * `LobbyView`'s problem; whether the arithmetic is right is this file's, and it is provable.
 */

// spec: SPEC-157
// spec: SPEC-162

/**
 * How many diagrams stand in the nearest arc, at full size and full detail.
 *
 * Was 7 through L1. The owner's own v1 framing (plan/lobby-v1.md) is explicit: "the latest 3
 * diagram models in front of the user" — L2 narrows the near rank to match, a smaller instance
 * of the identical arc-and-ranks shape rather than a different design (plan/lobby-v1.md, "The
 * latest 3 in front is a tuning value, not a spec"). `ARC_SWEEP_RADIANS` is retuned alongside it,
 * below, for the same reason it was tuned the first time: three miniatures across the old 2.0
 * sweep read as sparse, and only looking at a plan view says by how much.
 */
export const SLOTS_PER_RANK = 3;

/** Radius of the nearest arc, in metres. Comfortably within reach of a standing viewer. */
export const NEAR_RADIUS_METRES = 2.2;

/** Each rank behind the first sits this much further out. */
export const RANK_DEPTH_METRES = 1.5;

/**
 * Total angle the arc spans, in radians.
 *
 * **Tuned by rendering a top-down plan and looking at it, not by reasoning about the number.**
 * Nothing in this project can see a rendered pixel — `NullEngine` never rasterises — so a layout
 * is exactly the kind of thing that is easy to get numerically right and visually wrong. That was
 * true at 7-per-rank (2.4 put the outermost miniatures at ±68 degrees, almost beside the viewer;
 * 1.7 made seven of them nearly touch; 2.0 landed on ~0.7 m between neighbours, "a shelf rather
 * than a queue") and it is true again now that the near rank is 3 (plan/lobby-v1.md, SLOTS_PER_RANK's
 * own doc comment): the same 2.0 sweep spread across only three slots put 1.3 m between them —
 * comfortable, but no longer "in front of the user," more like flanking. Re-plotted the same way:
 * 1.2 gave that same over-wide 1.3 m gap and a 69-degree end-to-end span; 0.9 gave 51.6 degrees
 * and 0.98 m gaps, still wider than a shelf calls for. 0.6 lands within a centimetre of the
 * original ~0.7 m neighbour spacing (0.66 m) at a 34-degree end-to-end span — three miniatures
 * squarely in front of the viewer rather than spread to the sides, which is exactly what "3 in
 * front" asked for. See the rendered comparison in the L2 delivery notes
 * (plan/lobby-v1.md) for the three candidates actually plotted.
 *
 * Still unconfirmed on hardware: what is comfortable to look at is a headset question, and this
 * is a plan view of it.
 */
export const ARC_SWEEP_RADIANS = 0.6;

/** A miniature in the near rank is this tall, in metres. Ranks behind scale down from it. */
export const NEAR_HEIGHT_METRES = 0.55;

/**
 * How far back a rank can be and still be worth building geometry for. Beyond this a diagram is
 * still *placed* — it has a slot, and rotating brings it forward — but nothing is rendered for
 * it, which is what keeps a hundred diagrams from being a hundred draws.
 */
export const DETAIL_RANK_LIMIT = 2;

export interface LobbySlot {
  /** Index into the ordered diagram list this slot belongs to. */
  readonly index: number;

  /** 0 is the near arc; 1 is directly behind it, and so on. */
  readonly rank: number;

  readonly x: number;
  readonly y: number;
  readonly z: number;

  /** Uniform scale, relative to a near-rank miniature at 1. */
  readonly scale: number;

  /**
   * Whether this slot should have geometry built for it at all. False is not "invisible" — it is
   * "not built", which is the difference between a lobby that scales and one that merely looks
   * like it does.
   */
  readonly detailed: boolean;
}

/**
 * The slot for one position in the ordered list, given how many ranks have been rotated past.
 *
 * `rotation` is a whole number of ranks, not an angle: bringing the next rank forward is a
 * discrete move, so a diagram is either in the near arc or it is not. A continuous spin would
 * leave miniatures stopped between ranks at sizes nobody chose.
 */
export function slotFor(index: number, rotation: number = 0, count: number = Infinity): LobbySlot {
  const shifted = index - rotation * SLOTS_PER_RANK;

  // A slot rotated *past* — behind the viewer — keeps a real position rather than being clamped
  // to rank 0, so rotating back returns it to exactly where it was. Clamping here would make
  // rotation lossy and the lobby would visibly reshuffle every time somebody changed their mind.
  const rank = Math.floor(shifted / SLOTS_PER_RANK);
  const withinRank = shifted - rank * SLOTS_PER_RANK;

  const radius = NEAR_RADIUS_METRES + rank * RANK_DEPTH_METRES;

  // SPACING IS CONSTANT ACROSS RANKS; WHAT MOVES IS WHERE THE OCCUPIED SPAN IS CENTRED.
  //
  // Spreading each rank across the whole sweep instead would make a rank holding three diagrams
  // fan them out twice as far apart as a rank holding seven, so the lobby would visibly breathe
  // as you rotated through it. And a rank holding one would put that diagram at the left-hand end
  // of the sweep rather than in front of you — which is exactly what somebody who owns a single
  // diagram would see on arrival. Caught by a test rather than by looking at it, which is the
  // only way it could have been caught here at all: NullEngine never rasterises.
  const absoluteRank = Math.floor(index / SLOTS_PER_RANK);
  const inThisRank = Number.isFinite(count)
    ? Math.min(SLOTS_PER_RANK, Math.max(1, count - absoluteRank * SLOTS_PER_RANK))
    : SLOTS_PER_RANK;

  const step = SLOTS_PER_RANK > 1 ? ARC_SWEEP_RADIANS / (SLOTS_PER_RANK - 1) : 0;
  const occupiedSpan = (inThisRank - 1) * step;
  const angle = -occupiedSpan / 2 + withinRank * step;

  // Further ranks are smaller than perspective alone would make them. Perspective already
  // shrinks them; this shrinks them a little more, so depth reads as depth rather than as a
  // diagram that happens to be far away.
  const scale = NEAR_RADIUS_METRES / radius;

  return {
    index,
    rank,
    x: Math.sin(angle) * radius,
    // Lifted with distance so a further rank is visible over the one in front rather than hidden
    // behind it. Without this the arc-and-ranks shape degenerates into "one arc plus rumours".
    y: rank * 0.35,
    z: Math.cos(angle) * radius,
    scale,
    detailed: rank >= 0 && rank <= DETAIL_RANK_LIMIT,
  };
}

/**
 * Every slot for a list of that length, at that rotation.
 *
 * Returns a slot for every diagram, including ones nothing will be built for — the caller needs
 * to know a diagram exists and where it would be, even while it is too far back to draw.
 */
export function layout(count: number, rotation: number = 0): LobbySlot[] {
  const slots: LobbySlot[] = [];

  for (let index = 0; index < count; index += 1) {
    slots.push(slotFor(index, rotation, count));
  }

  return slots;
}

/**
 * How many ranks a list of this length occupies — what a caller needs to stop rotating past the
 * end.
 */
export function rankCount(count: number): number {
  return Math.max(1, Math.ceil(count / SLOTS_PER_RANK));
}

/**
 * Clamps a proposed rotation to the ranks that actually exist.
 *
 * Rotating past the last rank would leave the viewer facing nothing at all, with no cue about
 * which way to go back — the one failure mode of a rolodex you cannot see the ends of.
 */
export function clampRotation(rotation: number, count: number): number {
  return Math.min(Math.max(rotation, 0), rankCount(count) - 1);
}
